const { Type } = require('@sinclair/typebox');
const Blockly = require('blockly');
const { moduleRegistration } = require('@sstdev/lib_metadata-config');
const setupAddCodeProperties = require('../../../../helpers/addCodeProperties');
const { validationReporter } = require('../../../../helpers/validationReporter');

const TOOLTIP =
    'Toggle component used to toggle between two values. Can optionally be configured to have a neutral option.';

/**
 * @param {*} color
 * @param {import('blockly').Block} blocklyBlock
 */
function addBlockSpecificFields(color, blocklyBlock, mdcBlock, line1OfInputs) {
    blocklyBlock.setTooltip(TOOLTIP);
    blocklyBlock.setInputsInline(true);

    line1OfInputs.appendField('  Property Name:').appendField(new Blockly.FieldTextInput('title'), 'propertyName');

    blocklyBlock
        .appendDummyInput()
        .setAlign(Blockly.ALIGN_RIGHT)
        .appendField('  Negative (false) Option:')
        .appendField(new Blockly.FieldTextInput(''), 'toggleOption1');
    blocklyBlock
        .appendDummyInput()
        .setAlign(Blockly.ALIGN_RIGHT)
        .appendField('  Positive (true) Option:')
        .appendField(new Blockly.FieldTextInput('true'), 'toggleOption2');
    blocklyBlock
        .appendDummyInput()
        .setAlign(Blockly.ALIGN_RIGHT)
        .appendField('  Default Value:')
        .appendField(new Blockly.FieldTextInput('false'), 'defaultValue');

    /**
     * Making the neutral option a string makes for an easy way for us to manage toggle values and state if the neutral option is set.
     * Instead of writing a bunch of logic to handle the neutral option as a boolean, we can just match the string value of the neutral option.
     * This allows us to handle the toggle state and set default values on useFormControl, all using the same neutral option string.
     * @see https://sstdev.visualstudio.com/DefaultCollection/Backbone/_git/silo_ui/?path=/lib_ui-components/src/hooks/useToggle.js
     * @see https://sstdev.visualstudio.com/DefaultCollection/Backbone/_git/silo_ui/?path=/lib_ui-components/src/formElements/Toggle.js
     */
    blocklyBlock
        .appendDummyInput()
        .setAlign(Blockly.ALIGN_RIGHT)
        .appendField('  Neutral Option:')
        .appendField(new Blockly.FieldTextInput(''), 'neutralOption');
    blocklyBlock
        .appendDummyInput()
        .setAlign(Blockly.ALIGN_RIGHT)
        .appendField('  Label Beside Switch:')
        .appendField(new Blockly.FieldCheckbox('FALSE'), 'labelBesideSwitch');
    blocklyBlock
        .appendDummyInput()
        .setAlign(Blockly.ALIGN_RIGHT)
        .appendField('  Tooltip:')
        .appendField(new Blockly.FieldTextInput(''), 'tooltip');
}

/**
 * @param {{[key: string]: any}} code
 * @param {import('blockly').Block} blocklyBlock
 * @return {{[key: string]: any}}
 */
function convertCustomFieldsToCode(code, blocklyBlock) {
    const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);
    const propertyName = blocklyBlock.getFieldValue('propertyName');
    const opt1 = blocklyBlock.getFieldValue('toggleOption1');
    const opt2 = blocklyBlock.getFieldValue('toggleOption2');
    const neutralOption = blocklyBlock.getFieldValue('neutralOption');
    let defaultValue = blocklyBlock.getFieldValue('defaultValue');

    // Validate the combination of defaultValue and neutralOption
    if (defaultValue && defaultValue !== 'false' && neutralOption) {
        validationReporter(
            'The default value and neutral option cannot both be set. Use one or the other.',
            blocklyBlock.id
        );
    }

    // Normalize defaultValue based on the options and neutralOption
    if (defaultValue === 'true' || defaultValue === opt2) {
        defaultValue = true;
    } else if (defaultValue === 'false' || defaultValue === opt1 || defaultValue === undefined) {
        defaultValue = false;
    } else if (!neutralOption) {
        // If defaultValue is not recognized and there's no neutral option
        validationReporter(
            `The default value "${defaultValue}" is not valid. Use "${opt1}", "${opt2}", or leave it undefined.`,
            blocklyBlock.id
        );
        defaultValue = false; // Fallback to a safe default
    }

    // Add properties for code generation
    addCodeProperties([
        { key: 'propertyName', handler: () => propertyName },
        { key: 'toggleValues', handler: () => [opt1, opt2] },
        { key: 'neutralOption', handler: () => neutralOption },
        { key: 'defaultValue', handler: () => defaultValue },
        { key: 'labelBesideSwitch', handler: value => value === 'TRUE' },
        'tooltip'
    ]);

    return code;
}

/** Builds the TypeBox schema fragment for the Toggle form element. */
function asDataDictionary(hNode, context) {
    const { id, title, propertyName, toggleValues, defaultValue, neutralOption, required } = hNode;
    //This block tells us nothing about namespaces/relations
    const subDataDictionary = {};

    // If neutralOption is set, the toggle has a 3rd state (neither true nor false), so it should NOT be required.
    // Otherwise, respect the required flag from the metadata.
    const effectiveRequired = neutralOption ? false : required;
    const { isOptional, schemaOptions } = moduleRegistration.resolveFieldFlags(effectiveRequired, context);

    // Build the boolean schema with toggle-specific metadata
    const booleanOptions = {
        title: title,
        'x-id': id,
        'x-dataType': 'boolean',
        'x-propertyName': propertyName,
        ...schemaOptions,
        'x-toggleValues': toggleValues,
        'x-neutralOption': neutralOption
    };

    // Add default value if provided
    if (defaultValue !== undefined && defaultValue !== null) {
        booleanOptions.default = defaultValue;
    }

    let toggleSchema = Type.Boolean(booleanOptions);
    if (isOptional) {
        toggleSchema = Type.Optional(toggleSchema);
    }

    // The partial type definition is a plain object with the property schema
    const partialTypeDefinition = {
        [propertyName]: toggleSchema
    };

    return [subDataDictionary, partialTypeDefinition];
}

module.exports = { asDataDictionary, addBlockSpecificFields, convertCustomFieldsToCode };
